// src/components/resources/calculators/video-dropoff/components/charts/NormalizedChart.tsx

/**
 * @file Normalized Chart Component
 * @description Chart component for displaying normalized dropoff rate data
 */

import {
  Area,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  ReferenceLine,
  AreaChart,
  Tooltip,
} from '~/lib/charts/client'
import type { ChartScale, Breakpoint, DropoffDataPoint } from '~/components/resources/calculators/video-dropoff/types'
import { CustomTooltip } from '~/components/resources/calculators/video-dropoff/components/charts/CustomTooltip'
import { formatTime } from '~/components/resources/calculators/video-dropoff/utils/formatters'

/**
 * Props for the NormalizedChart component
 */
interface NormalizedChartProps {
  /** Chart data prepared for rendering with proper typing (uses dropoff data) */
  data: DropoffDataPoint[]
  /** Video length in seconds */
  videoLength: number
  /** Chart scale (linear or log) */
  chartScale: ChartScale
  /** Whether to exclude video starts from display */
  excludeVideoStarts: boolean
  /** Breakpoints for reference lines */
  breakpoints: Breakpoint[]
}

/**
 * Normalized chart component for video dropoff rate analysis
 * @param {NormalizedChartProps} props - Component props
 * @returns {JSX.Element} Rendered normalized chart
 */
export const NormalizedChart = ({
  data,
  videoLength,
  chartScale,
  excludeVideoStarts,
  breakpoints
}: NormalizedChartProps): JSX.Element => {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart
        data={data}
        margin={{ top: 30, right: 20, left: 25, bottom: 20 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="rgb(156, 163, 175)" opacity={0.2} />
        <XAxis 
          dataKey="timestamp"
          type="number"
          domain={
            excludeVideoStarts && data.length > 0
              ? [data[0]?.timestamp ?? 0, videoLength]
              : [0, videoLength]
          }
          tickFormatter={(value) => formatTime(value as number)}
          label={{ 
            value: "Video Timeline (seconds)", 
            position: "insideBottom", 
            offset: 0,
            style: { fill: 'rgb(107, 114, 128)', fontSize: '11px' },
            dy: 15
          }}
          stroke="rgb(156, 163, 175)"
          tickLine={false}
          tick={{ fontSize: 11 }}
        />
        <YAxis
          dataKey="normalizedRate" // Plot normalized drop-off rate
          scale={chartScale} // Allow log scale if desired
          domain={
            excludeVideoStarts 
              ? chartScale === 'log'
                ? [0.1, 'auto']
                : ['dataMin', 'auto']
              : chartScale === 'log' 
                ? [0.1, 'auto'] 
                : [0, 'auto']
          } // Set domain based on scale and excludeVideoStarts setting
          tickFormatter={(value) => `${(value as number).toFixed(1)}%`}
          allowDataOverflow={true}
          stroke="rgb(156, 163, 175)"
          tickLine={false}
          tick={{ fontSize: 11 }}
          width={40}
          label={{
            value: "Norm. Drop-off (%/sec)",
            angle: -90,
            style: { fill: 'rgb(107, 114, 128)', fontSize: '12px' },
            offset: 0,
            dx: -35
          }}
        />
        <Tooltip 
          content={<CustomTooltip videoLength={videoLength} activeView="normalizedDropoff" />}
          cursor={{ stroke: 'rgba(249, 115, 22, 0.5)', strokeWidth: 1, strokeDasharray: '5 5' }} // Orange cursor
          animationDuration={200}
          animationEasing="ease-in-out"
        />
        
        <Area
          type="monotone"
          dataKey="normalizedRate"
          stroke="rgb(249, 115, 22)"
          strokeWidth={2}
          fill="rgba(249, 115, 22, 0.2)"
          activeDot={{ r: 8, strokeWidth: 0, fill: 'rgb(249, 115, 22)' }}
          name="Normalized Drop-off Rate"
          animationDuration={1500}
          animationEasing="ease-in-out"
        />
        
        {/* Breakpoint markers */}
        {breakpoints.slice(excludeVideoStarts ? 1 : 0).map(breakpoint => {
          const timePosition = breakpoint.isPercentile
            ? (breakpoint.value / 100) * videoLength
            : breakpoint.value
          
          // Skip if this is exactly at time 0
          if (timePosition === 0) return null
          
          return (
            <ReferenceLine
              key={breakpoint.id}
              x={timePosition}
              stroke="rgba(79, 70, 229, 0.4)"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
          )
        })}
      </AreaChart>
    </ResponsiveContainer>
  )
}
